import type { ChatMessages } from "@storytell/cp-client";
import type { ParsedWebCitation } from "./citationUtils";
import { parseKBCitation, parseWebCitation } from "./citationUtils";

/**
 * Collection of web search results organized by tool ID for efficient lookups
 */
export interface WebSearchResultsCollection {
  byToolId: Map<string, ChatMessages.WebSearchResult>;
}

/**
 * Simple asset search results container for backward compatibility
 */
export interface SimpleAssetSearchResults {
  assets: ChatMessages.AssetSearchResultItem[];
}

/**
 * Collection of KB search results organized for efficient citation lookups
 */
export interface KBSearchResultsCollection {
  byToolId: Map<string, ChatMessages.AssetSearchResults | SimpleAssetSearchResults>;
  byChunkId: Map<string, ChunkWithAssetInfo>;
}

/**
 * Chunk result with its parent asset information
 */
export interface ChunkWithAssetInfo {
  chunk: ChatMessages.ChunkResult;
  asset: ChatMessages.AssetSearchResultItem;
}

/**
 * Resolves a web citation to find the corresponding search result
 *
 * @param citation - The citation string (e.g., "web_toolexecution_abc123_22")
 * @param collection - The collection of web search results
 * @returns The matching WebSearchResultItem, or null if not found
 */
export const resolveWebCitation = (
  citation: string,
  collection: WebSearchResultsCollection | undefined,
): ChatMessages.WebSearchResultItem | null => {
  // Parse the citation to extract toolId and index
  const parsed = parseWebCitation(citation);
  if (!parsed || !collection) {
    return null;
  }

  // Find the WebSearchResult by toolId
  const webSearchResult = collection.byToolId.get(parsed.toolId);
  if (!webSearchResult?.results) {
    return null;
  }

  // Find the specific result by index (1-based indexing)
  const resultItem = webSearchResult.results.find((result) => result.index === parsed.index);

  return resultItem || null;
};

/**
 * Validates if a parsed citation can be resolved in the collection
 *
 * @param parsed - The parsed citation object
 * @param collection - The collection of web search results
 * @returns True if the citation can be resolved, false otherwise
 */
export const canResolveCitation = (
  parsed: ParsedWebCitation,
  collection: WebSearchResultsCollection | undefined,
): boolean => {
  if (!collection) return false;

  const webSearchResult = collection.byToolId.get(parsed.toolId);
  if (!webSearchResult?.results) return false;

  return webSearchResult.results.some((result) => result.index === parsed.index);
};

/**
 * Gets all available citation indices for a given tool ID
 *
 * @param toolId - The tool ID to get indices for
 * @param collection - The collection of web search results
 * @returns Array of available indices, or empty array if none found
 */
export const getAvailableIndices = (toolId: string, collection: WebSearchResultsCollection | undefined): number[] => {
  if (!collection) return [];

  const webSearchResult = collection.byToolId.get(toolId);
  if (!webSearchResult?.results) return [];

  return webSearchResult.results.map((result) => result.index).sort((a, b) => a - b);
};

/**
 * Resolves a KB citation to find the corresponding chunk and asset data
 *
 * @param citation - The citation string (e.g., "asset_abc123_cxp_24")
 * @param collection - The collection of KB search results
 * @returns The matching chunk with asset info, or null if not found
 */
export const resolveKBCitation = (
  citation: string,
  collection: KBSearchResultsCollection | undefined,
): ChunkWithAssetInfo | null => {
  if (!collection) return null;

  const chunkData = collection.byChunkId.get(citation);
  if (chunkData) return chunkData;

  const parsed = parseKBCitation(citation);
  if (!parsed || parsed.position === undefined) return null;

  for (const [_, results] of collection.byToolId) {
    for (const asset of results.assets) {
      if (asset.assetId === parsed.assetId) {
        const chunk = asset.chunks.find((c) => c.position === parsed.position);
        if (chunk) {
          return { chunk, asset };
        }
      }
    }
  }

  return null;
};

/**
 * Resolves a KB citation to find the asset metadata (for legacy citations without chunks)
 * This provides backward compatibility for citations created before chunk data was included
 *
 * @param citation - The citation string (e.g., "asset_abc123_cxp_24")
 * @param collection - The collection of KB search results
 * @returns The matching asset info, or null if not found
 */
export const resolveKBCitationAsset = (
  citation: string,
  collection: KBSearchResultsCollection | undefined,
): ChatMessages.AssetSearchResultItem | null => {
  if (!collection) return null;

  const parsed = parseKBCitation(citation);
  if (!parsed) return null;

  for (const [_, results] of collection.byToolId) {
    for (const asset of results.assets) {
      if (asset.assetId === parsed.assetId) {
        return asset;
      }
    }
  }

  return null;
};

/**
 * Builds a KB citation index from search results for efficient lookups
 * Handles both protobuf oneOf format (future) and current flattened format
 *
 * @param searchResult - The search result containing asset search results
 * @returns Collection with indexes for O(1) chunk lookups
 */
export const buildKBCitationIndex = (
  searchResult: ChatMessages.SearchResult | undefined,
): KBSearchResultsCollection => {
  const collection: KBSearchResultsCollection = {
    byToolId: new Map(),
    byChunkId: new Map(),
  };

  if (!searchResult) return collection;

  const assets = searchResult.results?.value?.assets || (searchResult as any).assets?.assets || [];

  if (assets.length === 0) return collection;

  // Create a simple asset search results object for the collection
  const assetResults: SimpleAssetSearchResults = {
    assets: assets,
  };

  const toolId = searchResult.toolId || "unknown";
  collection.byToolId.set(toolId, assetResults);

  for (const asset of assets) {
    for (const chunk of asset.chunks || []) {
      collection.byChunkId.set(chunk.chunkId, { chunk, asset });
    }
  }

  return collection;
};
